import { logger } from "@/utils/logger";
/**
 * Performance Timing Utilities
 *
 * Provides instrumentation for measuring and tracking performance
 * in the DICOM viewer. Useful for:
 * - Identifying slow operations
 * - Tracking load times
 * - Measuring render performance
 * - Detecting performance regressions
 *
 * Usage:
 *   // Mark start of operation
 *   perfMark('dicom-load-start');
 *
 *   // ... operation ...
 *
 *   // Measure and log duration
 *   const duration = perfMeasure('dicom-load', 'dicom-load-start');
 *   logger.debug(`DICOM load took ${duration}ms`);
 *
 *   // Or use the timer helper
 *   const end = startTimer('load-series');
 *   await loadSeries();
 *   const duration = end(); // Logs and returns duration
 */

// Performance marks storage for browsers without Performance API support
const marks: Record<string, number> = {};
const measures: Array<{ name: string; duration: number; timestamp: number }> = [];

// Whether to log performance metrics to console
let debugMode = false;

/**
 * Enable/disable debug logging
 */
export function setPerformanceDebug(enabled: boolean): void {
  debugMode = enabled;
}

/**
 * Mark the start of a performance measurement
 */
export function perfMark(name: string): void {
  const now = performance.now();
  marks[name] = now;

  if (typeof performance.mark === "function") {
    try {
      performance.mark(name);
    } catch {
      // Ignore errors
    }
  }

  if (debugMode) {
    logger.debug(`[Perf] Mark: ${name} @ ${now.toFixed(2)}ms`);
  }
}

/**
 * Measure duration since a mark
 * @returns Duration in milliseconds
 */
export function perfMeasure(measureName: string, startMark: string): number {
  const startTime = marks[startMark];
  const endTime = performance.now();

  if (startTime === undefined) {
    console.warn(`[Perf] No start mark found: ${startMark}`);
    return 0;
  }

  const duration = endTime - startTime;

  // Use Performance API if available
  if (typeof performance.measure === "function") {
    try {
      performance.measure(measureName, startMark);
    } catch {
      // Ignore errors
    }
  }

  // Store measurement
  measures.push({
    name: measureName,
    duration,
    timestamp: endTime,
  });

  if (debugMode) {
    logger.debug(`[Perf] ${measureName}: ${duration.toFixed(2)}ms`);
  }

  return duration;
}

/**
 * Start a timer and return a function to end it
 * @returns Function that ends timer and returns duration
 */
export function startTimer(name: string): () => number {
  const markName = `${name}-start-${Date.now()}`;
  perfMark(markName);

  return () => {
    const duration = perfMeasure(name, markName);
    delete marks[markName]; // Clean up
    return duration;
  };
}

/**
 * Get all recorded measurements
 */
export function getPerformanceMeasures(): Array<{
  name: string;
  duration: number;
  timestamp: number;
}> {
  return [...measures];
}

/**
 * Clear all measurements
 */
export function clearPerformanceMeasures(): void {
  measures.length = 0;
  Object.keys(marks).forEach((key) => delete marks[key]);

  if (typeof performance.clearMarks === "function") {
    try {
      performance.clearMarks();
      performance.clearMeasures();
    } catch {
      // Ignore errors
    }
  }
}

/**
 * Get summary statistics for a measurement name
 */
export function getPerformanceStats(name: string): {
  count: number;
  min: number;
  max: number;
  avg: number;
  p50: number;
  p90: number;
  p99: number;
} | null {
  const matching = measures.filter((m) => m.name === name);

  if (matching.length === 0) return null;

  const durations = matching.map((m) => m.duration).sort((a, b) => a - b);
  const count = durations.length;
  const sum = durations.reduce((a, b) => a + b, 0);

  return {
    count,
    min: durations[0],
    max: durations[count - 1],
    avg: sum / count,
    p50: durations[Math.floor(count * 0.5)],
    p90: durations[Math.floor(count * 0.9)],
    p99: durations[Math.floor(count * 0.99)],
  };
}

/**
 * Log a performance summary to console
 */
export function logPerformanceSummary(): void {
  const names = Array.from(new Set(measures.map((m) => m.name)));

  console.group("[Perf] Performance Summary");

  names.forEach((name) => {
    const stats = getPerformanceStats(name);
    if (stats) {
      logger.debug(
        `${name}: count=${stats.count}, avg=${stats.avg.toFixed(2)}ms, ` +
          `min=${stats.min.toFixed(2)}ms, max=${stats.max.toFixed(2)}ms, ` +
          `p50=${stats.p50.toFixed(2)}ms, p90=${stats.p90.toFixed(2)}ms`,
      );
    }
  });

  console.groupEnd();
}

// =============================================================================
// Web Vitals Integration
// =============================================================================

/**
 * Track Core Web Vitals using PerformanceObserver
 * Call this once on app initialization
 */
export function trackWebVitals(callback?: (metric: { name: string; value: number }) => void): void {
  if (typeof PerformanceObserver === "undefined") {
    console.warn("[Perf] PerformanceObserver not supported");
    return;
  }

  // Largest Contentful Paint (LCP)
  try {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1];
      const lcp = lastEntry.startTime;

      if (debugMode) {
        logger.debug(`[Perf] LCP: ${lcp.toFixed(2)}ms`);
      }
      callback?.({ name: "LCP", value: lcp });
    });
    lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
  } catch {
    // Not supported
  }

  // First Input Delay (FID)
  try {
    const fidObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const firstEntry = entries[0] as PerformanceEventTiming;
      const fid = firstEntry.processingStart - firstEntry.startTime;

      if (debugMode) {
        logger.debug(`[Perf] FID: ${fid.toFixed(2)}ms`);
      }
      callback?.({ name: "FID", value: fid });
    });
    fidObserver.observe({ type: "first-input", buffered: true });
  } catch {
    // Not supported
  }

  // Cumulative Layout Shift (CLS)
  try {
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!(entry as any).hadRecentInput) {
          clsValue += (entry as any).value;
        }
      }

      if (debugMode) {
        logger.debug(`[Perf] CLS: ${clsValue.toFixed(4)}`);
      }
      callback?.({ name: "CLS", value: clsValue });
    });
    clsObserver.observe({ type: "layout-shift", buffered: true });
  } catch {
    // Not supported
  }

  // Long Tasks (for Total Blocking Time estimation)
  try {
    const longTaskObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      entries.forEach((entry) => {
        const blockingTime = entry.duration - 50; // Tasks > 50ms block main thread

        if (debugMode && blockingTime > 0) {
          logger.debug(
            `[Perf] Long Task: ${entry.duration.toFixed(2)}ms (blocking: ${blockingTime.toFixed(2)}ms)`,
          );
        }
        callback?.({ name: "LongTask", value: entry.duration });
      });
    });
    longTaskObserver.observe({ type: "longtask", buffered: true });
  } catch {
    // Not supported
  }
}

// =============================================================================
// DICOM-specific Timing Helpers
// =============================================================================

/** Standard event names for DICOM loading */
export const PERF_EVENTS = {
  // Series loading
  SERIES_LOAD_START: "series-load-start",
  SERIES_LOAD_END: "series-load",
  SERIES_FIRST_FRAME: "series-first-frame",

  // Viewport
  VIEWPORT_INIT: "viewport-init",
  VIEWPORT_RENDER: "viewport-render",

  // Volume
  VOLUME_LOAD_START: "volume-load-start",
  VOLUME_LOAD_COMPLETE: "volume-load",
  VOLUME_FIRST_DISPLAYABLE: "volume-first-displayable",

  // User interactions
  SLICE_CHANGE: "slice-change",
  WINDOWING_CHANGE: "windowing-change",
  TOOL_SWITCH: "tool-switch",

  // Study navigation
  STUDY_OPEN: "study-open",
  STUDY_METADATA_LOAD: "study-metadata-load",
} as const;

/**
 * Track DICOM series loading performance
 */
export function trackSeriesLoad(seriesId: string): () => void {
  const eventName = `${PERF_EVENTS.SERIES_LOAD_START}-${seriesId.substring(0, 8)}`;
  perfMark(eventName);

  return () => {
    const duration = perfMeasure(PERF_EVENTS.SERIES_LOAD_END, eventName);

    if (debugMode) {
      logger.debug(
        `[Perf] Series ${seriesId.substring(0, 8)}... loaded in ${duration.toFixed(2)}ms`,
      );
    }
  };
}

/**
 * Export performance data for Playwright tests
 * This function can be called from the browser console or via Playwright
 */
export function exportPerformanceData(): {
  measures: Array<{ name: string; duration: number; timestamp: number }>;
  stats: Record<string, ReturnType<typeof getPerformanceStats>>;
} {
  const names = Array.from(new Set(measures.map((m) => m.name)));
  const stats: Record<string, ReturnType<typeof getPerformanceStats>> = {};

  names.forEach((name) => {
    stats[name] = getPerformanceStats(name);
  });

  return {
    measures: [...measures],
    stats,
  };
}

// Expose to window for Playwright access
if (typeof window !== "undefined") {
  (window as any).__perfTiming = {
    getMeasures: getPerformanceMeasures,
    getStats: getPerformanceStats,
    export: exportPerformanceData,
    log: logPerformanceSummary,
    setDebug: setPerformanceDebug,
  };
}
